import { Component, input, output } from '@angular/core';
import {
  ControlContainer,
  FormControlStatus,
  FormsModule,
  NgForm,
} from '@angular/forms';
import {
  NumberInputComponent,
  SelectInputComponent,
  InputLabelComponent,
  IconComponent,
} from '@navstation-portal/shared/ui/components';
import { IconNames } from '@navstation-portal/shared/data-model/ui';
import { ButtonModule } from 'primeng/button';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { Tooltip } from 'primeng/tooltip';
import { getIsoGradeOptionsByRow } from '@navstation-portal/reporting/data-access/offshore-report';
import { FuelSpecsTableVm } from '@navstation-portal/reporting/data-model/offshore-report';

export interface FuelSpecTableColumn {
  key: string;
  label: string;
  required?: boolean;
  min?: number;
  max?: number;
  error?: string;
}

@Component({
  selector: 're-fuel-specs-table',
  imports: [
    ButtonModule,
    SelectInputComponent,
    NumberInputComponent,
    FormsModule,
    InputLabelComponent,
    Tooltip,
    IconComponent,
  ],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  templateUrl: './fuel-specs-table.component.html',
})
export class FuelSpecsTableComponent {
  object = Object;
  IconNames = IconNames;

  rows = input.required<FuelSpecTableColumn[]>();
  data = input.required<FuelSpecsTableVm>();
  fuelTypeOptionsByRow = input.required<SelectOption[][]>();
  fuelTypeChanged = output<number>();
  entryRemoved = output<number | undefined>();
  entryAdded = output<void>();
  fuelISOGradeChanged = output<{
    index: number;
    fuelType?: SelectOption;
    isoGrade: SelectOption;
  }>();
  rangeStatusChanged = output<{
    index: number;
    field: string;
    status: 'VALID' | 'INVALID';
  }>();

  getIsoGradeOptionsByRow(entryIndex: number): SelectOption[] {
    return getIsoGradeOptionsByRow(
      this.data().entries,
      entryIndex,
    ) as SelectOption[];
  }

  onFuelTypeChanged(index: number) {
    this.fuelTypeChanged.emit(index);
  }

  getIsoFuelGradeTooltip(
    disabled: boolean,
    input:
      | {
          status?: FormControlStatus;
          error?: string;
          value?: unknown;
        }
      | undefined,
    required: boolean,
  ): string {
    if (disabled || !input?.error) return '';

    const isInvalid = input.status === 'INVALID';
    const isMissingRequiredValue =
      required &&
      (input.value === undefined ||
        input.value === null ||
        input.value === '' ||
        input.value !== input.value);

    return isInvalid || isMissingRequiredValue ? input.error : '';
  }

  onFuelISOGradeChanged(index: number, isoGrade: SelectOption | undefined) {
    const entry = this.data().entries[index];
    const fuelType = entry?.fuelType?.value;

    if (!fuelType || !isoGrade) return;

    this.fuelISOGradeChanged.emit({
      index,
      fuelType,
      isoGrade,
    });
  }

  removeEntry(index?: number) {
    this.entryRemoved.emit(index);
  }

  addEntry() {
    this.entryAdded.emit();
  }

  onNumberValueChanged(
    index: number,
    field: string,
    value: unknown,
    min?: number,
    max?: number,
    currentStatus?: FormControlStatus,
  ) {
    const hasRange = typeof min === 'number' || typeof max === 'number';

    if (!hasRange) return;
    if (typeof value !== 'number' || Number.isNaN(value)) return;

    const isBelowMin = typeof min === 'number' && value < min;
    const isAboveMax = typeof max === 'number' && value > max;
    const isOutOfRange = isBelowMin || isAboveMax;

    if (isOutOfRange) {
      this.rangeStatusChanged.emit({ index, field, status: 'INVALID' });
      return;
    }

    if (currentStatus === 'INVALID') {
      this.rangeStatusChanged.emit({ index, field, status: 'VALID' });
    }
  }
}
